import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/AppLayout';
import { S, Icon, PageHeader, BtnPrimary, BtnSecondary, LOCALE_CONFIG } from '../regureStyles';
import { useSelector, useDispatch } from 'react-redux';
import { getLoggedinUserAction } from '../../redux/actions/userActions';
import { LIST_ORG_REQUEST, LIST_ORG_SUCCESS } from '../../redux/constants/orgConstants';
import {
  useTenantSettings, VocabularySection, ModulesSection, BrandingSection, StorageSection,
  DocumentSettingsSection,
  CommunicationsTextSection,
  SignatoriesSection,
} from './TenantSettingsSections';
import { usePermission } from '../../hooks/useAccess';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const LS_KEY = 'regure_locale_settings';

const CURRENCIES = [
  { code: 'CHF', label: 'Swiss Franc (CHF)', symbol: 'CHF', flag: 'CH' },
  { code: 'EUR', label: 'Euro (EUR)',         symbol: '€',   flag: 'EU' },
  { code: 'USD', label: 'US Dollar (USD)',    symbol: '$',   flag: 'US' },
  { code: 'GBP', label: 'British Pound (GBP)', symbol: '£', flag: 'GB' },
  // The list is not the bound — withResolved() below shows a tenant's
  // configured currency whether or not it is here, which is the whole point
  // of that helper. These are here so the currencies the platform's tenants
  // actually trade in can be PICKED, not only inherited.
  { code: 'AED', label: 'UAE Dirham (AED)',      symbol: 'AED', flag: 'AE' },
  { code: 'BHD', label: 'Bahraini Dinar (BHD)',  symbol: 'BHD', flag: 'BH' },
  { code: 'KWD', label: 'Kuwaiti Dinar (KWD)',   symbol: 'KWD', flag: 'KW' },
  { code: 'OMR', label: 'Omani Rial (OMR)',      symbol: 'OMR', flag: 'OM' },
  { code: 'QAR', label: 'Qatari Riyal (QAR)',    symbol: 'QAR', flag: 'QA' },
  { code: 'SAR', label: 'Saudi Riyal (SAR)',     symbol: 'SAR', flag: 'SA' },
];

// A date format is an ordering and a separator. It is not a nationality.
// These read "(Swiss/German)", "(European)" and "(US)", which is wrong twice
// over: DD/MM/YYYY is used across most of the world, and a tenant who picks
// it is told they have selected somebody else's country.
//
// `labelKey` is explicit because it used to be derived from the format
// string with `.replace(/[/.-]/g, '_')` — which maps BOTH 'DD.MM.YYYY' and
// 'DD/MM/YYYY' to `dateFmt_DD_MM_YYYY`. The `_2` key was unreachable, so a
// DD/MM/YYYY tenant read "DD.MM.YYYY (Swiss/German)" under a card whose
// heading said DD/MM/YYYY: the wrong separator AND the wrong country.
const DATE_FORMATS = [
  { fmt: 'DD.MM.YYYY', labelKey: 'dateFmtDayMonthYearDot',   label: 'Day.Month.Year',  example: '01.03.2026' },
  { fmt: 'DD/MM/YYYY', labelKey: 'dateFmtDayMonthYearSlash', label: 'Day/Month/Year',  example: '01/03/2026' },
  { fmt: 'MM/DD/YYYY', labelKey: 'dateFmtMonthDayYearSlash', label: 'Month/Day/Year',  example: '03/01/2026' },
  { fmt: 'YYYY-MM-DD', labelKey: 'dateFmtYearMonthDayIso',   label: 'Year-Month-Day (ISO 8601)', example: '2026-03-01' },
];

// Match the actual i18n resources declared in frontend/src/i18n/index.js.
// Italian bundles were never authored — selecting `it` from a dropdown that
// listed it made i18n silently fall back to English on the login screen
// while other panels appeared to render French, producing the "German
// missing / cannot switch" reports (ARGOS A7.6). Keep this list in sync
// with i18n/index.js `supportedLngs`.
const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
];

const REGIONS = [
  { code: 'CH', label: 'Switzerland' },
  { code: 'DE', label: 'Germany' },
  { code: 'ES', label: 'Spain' },
  { code: 'FR', label: 'France' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'CO', label: 'Colombia' },
  { code: 'EU', label: 'European Union' },
  { code: 'US', label: 'United States' },
];

// A configured value the shipped list does not carry must still be shown as
// itself. Appending it beats silently rendering the first option, which is
// how a tenant configured Europe/Madrid could be shown "UTC" and a tenant
// configured ES could be shown "United States" — a default presented with
// all the confidence of a setting.
function withResolved(list, value) {
  if (!value) return list;
  return list.some(o => o.code === value) ? list : [...list, { code: value, label: value }];
}

// Preview the candidate currency the way that currency is actually written.
// The old preview hardcoded "12,345" for every option, so a Swiss tenant —
// whose separator is an apostrophe and who sees CHF 660'359 on every other
// screen — was shown CHF 12,345 here, in a control whose entire job is to
// demonstrate formatting.
const PREVIEW_LOCALE = { CHF: 'de-CH', EUR: 'de-DE', GBP: 'en-GB', USD: 'en-US' };
function formatCurrencyIn(code, amount) {
  if (!code) return '—';
  try {
    return new Intl.NumberFormat(PREVIEW_LOCALE[code] || 'en-US', {
      style: 'currency', currency: code, maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${code} ${amount}`;
  }
}

const TIMEZONES = [
  { code: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { code: 'Europe/Zurich', label: 'Europe/Zurich (CET/CEST)' },
  { code: 'Europe/Berlin', label: 'Europe/Berlin (CET/CEST)' },
  { code: 'Europe/Paris', label: 'Europe/Paris (CET/CEST)' },
  { code: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { code: 'Europe/Madrid', label: 'Europe/Madrid (CET/CEST)' },
  { code: 'Europe/Lisbon', label: 'Europe/Lisbon (WET/WEST)' },
  { code: 'Europe/Rome', label: 'Europe/Rome (CET/CEST)' },
  { code: 'Europe/Brussels', label: 'Europe/Brussels (CET/CEST)' },
  { code: 'Europe/Dublin', label: 'Europe/Dublin (GMT/IST)' },
  { code: 'Europe/Stockholm', label: 'Europe/Stockholm (CET/CEST)' },
  { code: 'Europe/Warsaw', label: 'Europe/Warsaw (CET/CEST)' },
  { code: 'Europe/Amsterdam', label: 'Europe/Amsterdam (CET/CEST)' },
  { code: 'Europe/Vienna', label: 'Europe/Vienna (CET/CEST)' },
  { code: 'America/Bogota', label: 'America/Bogota (COT)' },
  { code: 'America/Mexico_City', label: 'America/Mexico_City (CST)' },
  { code: 'America/Sao_Paulo', label: 'America/Sao_Paulo (BRT)' },
  { code: 'America/New_York', label: 'America/New_York (EST/EDT)' },
  { code: 'America/Chicago', label: 'America/Chicago (CST/CDT)' },
  { code: 'America/Denver', label: 'America/Denver (MST/MDT)' },
  { code: 'America/Los_Angeles', label: 'America/Los_Angeles (PST/PDT)' },
  { code: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { code: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
  { code: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
  { code: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
  { code: 'Australia/Sydney', label: 'Australia/Sydney (AEDT/AEST)' },
  { code: 'Australia/Melbourne', label: 'Australia/Melbourne (AEDT/AEST)' },
];

const loadSettings = () => {
  try {
    const saved = localStorage.getItem(LS_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch { return null; }
};

const AccountSettingsScreen = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const loggedinUser = useSelector(state => state.loggedinUser);
  const userLogin    = useSelector(state => state.userLogin);
  const orgsList = useSelector(state => state.orgsList);

  // Try multiple paths to get user data
  const userData = loggedinUser?.userInfo?.data?.data
    || loggedinUser?.userInfo?.data?.user
    || loggedinUser?.userInfo?.user
    || loggedinUser?.userInfo
    || userLogin?.userInfo?.data?.user;

  // Find current user's org. Prefer orgsList (populated when the user is a
  // platform admin), fall back to a direct /orgs/:id fetch below so systemAdmins
  // whose orgsList isn't hydrated still see their profile.
  const userOrgId = userData?.org;
  const orgFromList = Array.isArray(orgsList?.orgs) && userOrgId ? orgsList.orgs.find(org => org._id === userOrgId) : null;
  const [orgFromFetch, setOrgFromFetch] = useState(null);
  const userOrg = orgFromList || orgFromFetch;
  const isOrgAdmin = ['org', 'orgAdmin', 'systemAdmin'].includes(userData?.role);

  // Tenant configuration (vocabulary / modules / branding / storage). The
  // hook owns the drafts and the single PATCH; the sections below are
  // presentation only. Config comes from Redux so a save re-renders the
  // whole app — nav labels and brand tokens included — without a reload.
  const tenantConfig = useSelector(state => state?.tenantConfig?.config) || null;
  const tenantLocale = tenantConfig?.locale || {};
  const tenant = useTenantSettings(tenantConfig);
  const canManageBilling = usePermission('orgs', 'update');

  const [activeTab, setActiveTab] = useState('locale');
  const [saved, setSaved]         = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving]       = useState(false);
  const [orgLanguage, setOrgLanguage] = useState(userOrg?.preferredLanguage || 'en');
  const [orgCountry,  setOrgCountry]  = useState(userOrg?.country  || '');
  const [orgIndustry, setOrgIndustry] = useState(userOrg?.industry || '');
  const [orgTimezone, setOrgTimezone] = useState(userOrg?.timezone || '');
  const [orgCurrency, setOrgCurrency] = useState(userOrg?.currency || '');
  // Editable org-profile fields (persisted via PATCH /tenant/org-profile).
  const [orgName,     setOrgName]     = useState(userOrg?.name     || '');
  const [orgAddress,  setOrgAddress]  = useState(userOrg?.address  || '');
  const [orgPhone,    setOrgPhone]    = useState(userOrg?.phone    || '');

  // Locale settings — seed from API preferences, fallback to localStorage, then defaults
  const apiPrefs = userData?.preferences || {};
  
  const lsDefaults = loadSettings() || {};
  // Currency and date format resolve exactly like region and timezone below:
  // the user's own override, then the TENANT's configuration, then nothing.
  // They used to fall back to a hardcoded platform value, so opening this
  // screen and pressing Save wrote USD / MM-DD-YYYY onto a tenant configured
  // EUR / DD-MM-YYYY — recreating the very fake override the schema change
  // removed. '' means "inherit from tenant" and is sent as a clear, not a value.
  const [currency,   setCurrency]   = useState(apiPrefs.currency   || tenantLocale.currency   || lsDefaults.currency   || '');
  const [dateFormat, setDateFormat] = useState(apiPrefs.dateFormat || tenantLocale.dateFormat || lsDefaults.dateFormat || '');
  const [language,   setLanguage]   = useState(apiPrefs.language   || lsDefaults.language   || 'en');
  // Region and timezone resolve the same way currency and date format do:
  // the user's own preference, then the TENANT's configured locale, then
  // whatever a previous session cached. There is deliberately no hardcoded
  // last resort — 'UTC' and 'US' were exactly that, and a Spanish tenant
  // configured Europe/Madrid was shown "UTC" as though it were their
  // setting. An unresolved value renders as an explicit "not configured"
  // option rather than as a confident wrong one.
  const [region,     setRegion]     = useState(apiPrefs.region   || tenantLocale.country  || window.REGURE_REGION   || lsDefaults.region   || '');
  const [timezone,   setTimezone]   = useState(apiPrefs.timezone || tenantLocale.timezone || window.REGURE_TIMEZONE || lsDefaults.timezone || '');

  // Platform settings
  const [notifications, setNotifications] = useState(apiPrefs.notifications !== undefined ? apiPrefs.notifications : (lsDefaults.notifications !== false));
  const [auditEmails,   setAuditEmails]   = useState(apiPrefs.auditEmails   !== undefined ? apiPrefs.auditEmails   : (lsDefaults.auditEmails   !== false));

  // TOTP 2FA enrollment state
  const [totpEnabled,    setTotpEnabled]    = useState(null);
  const [totpStep,       setTotpStep]       = useState('idle'); // idle | enrolling | verifying | disabling
  const [totpQR,         setTotpQR]         = useState(null);
  const [totpSecret,     setTotpSecret]     = useState(null);
  const [totpCode,       setTotpCode]       = useState('');
  const [totpError,      setTotpError]      = useState('');
  const [totpLoading,    setTotpLoading]    = useState(false);

  // Stripe billing portal
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError,   setPortalError]   = useState('');

  // Sync local state when userData preferences change (e.g., after save)
  useEffect(() => {
    if (userData?.preferences) {
      const prefs = userData.preferences;
      if (prefs.notifications !== undefined) setNotifications(prefs.notifications);
      if (prefs.auditEmails !== undefined) setAuditEmails(prefs.auditEmails);
      if (apiPrefs.currency) setCurrency(apiPrefs.currency);
      if (apiPrefs.dateFormat) setDateFormat(apiPrefs.dateFormat);
      if (apiPrefs.language) setLanguage(apiPrefs.language);
      if (apiPrefs.region) setRegion(apiPrefs.region);
    }
  }, [userData]);

  // The tenant config is fetched after mount, so the first render of this
  // screen can precede it. Adopt the tenant's locale when it lands — but
  // never over the user's own preference, and never over an edit they have
  // already made in this session (both are covered by only filling a value
  // that is still empty).
  useEffect(() => {
    if (!apiPrefs.timezone && tenantLocale.timezone) {
      setTimezone(prev => prev || tenantLocale.timezone);
    }
    if (!apiPrefs.region && tenantLocale.country) {
      setRegion(prev => prev || tenantLocale.country);
    }
  }, [apiPrefs.timezone, apiPrefs.region, tenantLocale.timezone, tenantLocale.country]);

  // Sync org language when org data changes
  useEffect(() => {
    if (userOrg?.preferredLanguage) {
      setOrgLanguage(userOrg.preferredLanguage);
    }
    if (userOrg?.country)  setOrgCountry(userOrg.country);
    if (userOrg?.industry) setOrgIndustry(userOrg.industry);
    if (userOrg?.timezone) setOrgTimezone(userOrg.timezone);
    if (userOrg?.currency) setOrgCurrency(userOrg.currency);
    if (userOrg?.name)     setOrgName(userOrg.name);
    if (userOrg?.address)  setOrgAddress(userOrg.address);
    if (userOrg?.phone)    setOrgPhone(userOrg.phone);
  }, [userOrg]);

  // Direct-fetch the tenant's org profile when not already in the orgsList
  // redux slice. Uses /tenant/org-profile (any authenticated user) instead
  // of /orgs/:id (admin-only) so systemAdmins and non-platform admins alike
  // see their org's name/address/phone/country. The old /orgs/:userOrgId
  // path 404'd because in this codebase req.user.org is a User _id (tenant
  // root), not an Org _id.
  useEffect(() => {
    if (orgFromList) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${BASE_URL}/tenant/org-profile`, { withCredentials: true });
        const org = resp?.data?.data;
        if (!cancelled && org) setOrgFromFetch(org);
      } catch { /* keep placeholder — user still sees other tabs */ }
    })();
    return () => { cancelled = true; };
  }, [orgFromList]);

  // Fetch TOTP status from backend on component mount
  useEffect(() => {
    const fetchTotpStatus = async () => {
      try {
        const resp = await axios.get(`${BASE_URL}/users/totp/status`, { withCredentials: true });
        setTotpEnabled(resp.data?.data?.totpEnabled || false);
      } catch (error) {
        // If endpoint not available or user not authenticated, default to false
        setTotpEnabled(false);
      }
    };
    fetchTotpStatus();
  }, []);
  const openBillingPortal = async () => {
    setPortalLoading(true); setPortalError('');
    try {
      const resp = await axios.post(`${BASE_URL}/packages/billing-portal`, {}, { withCredentials: true });
      if (resp.data?.data?.url) {
        window.location.href = resp.data.data.url;
      } else {
        setPortalError(t('screens.accountSettings.portalNoUrl'));
      }
    } catch (e) {
      setPortalError(e.response?.data?.message || t('screens.accountSettings.portalFailed'));
    } finally { setPortalLoading(false); }
  };

  const startTotpSetup = async () => {
    setTotpLoading(true); setTotpError('');
    try {
      const resp = await axios.post(`${BASE_URL}/users/totp/setup`, {}, { withCredentials: true });
      setTotpQR(resp.data.data.qrDataUri);
      setTotpSecret(resp.data.data.secret);
      setTotpStep('verifying');
    } catch (e) {
      setTotpError(e.response?.data?.message || t('screens.accountSettings.setupFailed'));
    } finally { setTotpLoading(false); }
  };

  const confirmTotpEnable = async () => {
    if (!totpCode.trim()) return;
    setTotpLoading(true); setTotpError('');
    try {
      await axios.post(`${BASE_URL}/users/totp/verify`, { token: totpCode }, { withCredentials: true });
      setTotpEnabled(true);
      setTotpStep('idle');
      setTotpCode('');
      setTotpQR(null);
    } catch (e) {
      setTotpError(e.response?.data?.message || t('screens.accountSettings.invalidCode'));
    } finally { setTotpLoading(false); }
  };

  const startTotpDisable = () => { setTotpStep('disabling'); setTotpCode(''); setTotpError(''); };

  const confirmTotpDisable = async () => {
    if (!totpCode.trim()) return;
    setTotpLoading(true); setTotpError('');
    try {
      await axios.post(`${BASE_URL}/users/totp/disable`, { token: totpCode }, { withCredentials: true });
      setTotpEnabled(false);
      setTotpStep('idle');
      setTotpCode('');
    } catch (e) {
      setTotpError(e.response?.data?.message || t('screens.accountSettings.invalidCode'));
    } finally { setTotpLoading(false); }
  };

  const handleSaveLocale = async () => {
    setSaveError('');
    const settings = { currency, dateFormat, language, region, timezone, notifications, auditEmails };
    localStorage.setItem(LS_KEY, JSON.stringify(settings));
    // Announce, do not write. LocaleProvider is the single writer of the
    // window.REGURE_* globals: it re-reads this key on the event below and
    // resolves it over the user record and the tenant config — including
    // the "left blank means inherit from the tenant" rule this screen used
    // to reimplement here. Two writers is how the platform ended up with
    // two different answers for the tenant's currency; see
    // utils/LocaleContext.
    window.dispatchEvent(new CustomEvent('regure-locale-changed'));
    if (language && i18n.language !== language) i18n.changeLanguage(language);
    try {
      await axios.patch(`${BASE_URL}/users/updateMe`, {
        preferences: { currency, dateFormat, language, region, timezone, notifications, auditEmails },
      }, { withCredentials: true });
      // Refresh loggedinUser so navigation/index.js's language-resolution
      // useEffect sees the new preferences.language on its next run.
      // Without this, tenantConfig.locale.language (e.g. 'fr' for ARGOS)
      // was overriding the user's selection because
      // resolvedLanguage = userLanguage || tenantLanguage — with userLanguage
      // still coming from stale Redux state, tenantLanguage won and the
      // display bounced back to French right after the user picked German.
      await dispatch(getLoggedinUserAction());
    } catch (e) {
      setSaveError(t('screens.accountSettings.syncedLocallyOnly'));
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleSavePlatformSettings = async () => {
    setSaving(true);
    setSaveError('');
    const settings = { currency, dateFormat, language, region, timezone, notifications, auditEmails };
    localStorage.setItem(LS_KEY, JSON.stringify(settings));
    try {
      await axios.patch(`${BASE_URL}/users/updateMe`, {
        preferences: { notifications, auditEmails },
      }, { withCredentials: true });
      // Reload user data so preferences update is reflected in UI
      await dispatch(getLoggedinUserAction());
    } catch (e) {
      setSaveError(t('screens.accountSettings.syncedLocallyOnly'));
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  // No reload needed — window globals + custom event trigger re-renders
  const handleApplyAndReload = () => handleSaveLocale();

  const handleSaveOrgLanguage = async () => {
    if (!userOrgId) {
      setSaveError(t('screens.accountSettings.orgIdSaveError'));
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      // Persist the editable org-profile fields (name/address/phone/country)
      // via the new /tenant/org-profile PATCH — writes to both the tenant
      // root User doc AND TenantConfig, no admin role required beyond
      // orgAdmin/systemAdmin (enforced server-side).
      const { data } = await axios.patch(`${BASE_URL}/tenant/org-profile`, {
        name:    orgName.trim(),
        address: orgAddress.trim(),
        phone:   orgPhone.trim(),
        country: orgCountry.trim().toUpperCase(),
      }, { withCredentials: true });
      // Re-seed the fetched-org state so the form reflects the persisted
      // values immediately (also covers server-side trimming/normalization).
      if (data?.data) setOrgFromFetch(data.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setSaveError(e?.response?.data?.message || t('screens.accountSettings.orgSaveFailed'));
    }
    setSaving(false);
  };

  // Tenant configuration is a Tenant Admin concern, so those tabs follow
  // the same gate the Organization tab already uses. Storage sits with
  // them because it reports the tenant's commercial limits, even though
  // it is read-only here.
  const TABS = [
    { id: 'locale',   label: t('screens.accountSettings.tabLocale'),  icon: 'globe' },
    { id: 'platform', label: t('screens.accountSettings.tabPlatform'), icon: 'settings' },
    ...(isOrgAdmin ? [
      { id: 'organization', label: t('screens.accountSettings.tabOrganization'), icon: 'building' },
      { id: 'vocabulary',   label: t('screens.tenantSettings.tabVocabulary'), icon: 'edit' },
      { id: 'modules',      label: t('screens.tenantSettings.tabModules'),    icon: 'grid' },
      { id: 'branding',     label: t('screens.tenantSettings.tabBranding'),   icon: 'pen-tool' },
      { id: 'documents',    label: t('screens.tenantSettings.tabDocuments', { defaultValue: 'Document Settings' }), icon: 'file-text' },
      { id: 'storage',      label: t('screens.tenantSettings.tabStorage'),    icon: 'folder' },
    ] : []),
    { id: 'profile',  label: t('screens.accountSettings.tabProfile'), icon: 'user' },
  ];

  // Tabs whose edits are saved by the tenant-config save button in the header.
  // 'documents' belongs here — omitting it would render the textarea with a
  // Save button that silently does nothing to it.
  const TENANT_CONFIG_TABS = new Set(['vocabulary', 'modules', 'branding', 'documents']);

  const inputStyle = { width: '100%', padding: '10px 12px', border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', background: '#fff', color: S.textPrimary };
  const selectStyle = { ...inputStyle, cursor: 'pointer' };
  const sectionLabel = (text) => (
    <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, marginTop: 20 }}>{text}</div>
  );

  const Toggle = ({ checked, onChange, label, desc }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: `1px solid ${S.borderLight}` }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 500, color: S.textPrimary }}>{label}</div>
        {desc && <div style={{ fontSize: 12, color: S.textMuted, marginTop: 2 }}>{desc}</div>}
      </div>
      <div onClick={() => onChange(!checked)} style={{ width: 44, height: 24, borderRadius: 12, background: checked ? S.burgundy : '#cbd5e1', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
        <div style={{ position: 'absolute', top: 2, left: checked ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
      </div>
    </div>
  );

  return (
    <AppLayout>
      <div style={{ flex: 1, overflow: 'auto' }}>
        <PageHeader
          title={t('screens.accountSettings.title')}
          subtitle={t('screens.accountSettings.subtitle')}
          actions={
            <div style={{ display: 'flex', gap: 10 }}>
              {saved && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#ecfdf5', borderRadius: 8, fontSize: 13, color: '#10b981', fontWeight: 500 }}>
                  <Icon name="check" size={14} color="#10b981" /> {t('screens.accountSettings.saved')}
                </div>
              )}
              {saveError && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#fffbeb', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
                  {saveError}
                </div>
              )}
              {activeTab === 'locale' && (
                <>
                  <BtnSecondary onClick={handleSaveLocale}><Icon name="check" size={15} />{t('screens.accountSettings.save')}</BtnSecondary>
                  <BtnPrimary onClick={handleApplyAndReload}><Icon name="check" size={15} />{t('screens.accountSettings.saveApply')}</BtnPrimary>
                </>
              )}
              {activeTab === 'platform' && (
                <BtnPrimary onClick={handleSavePlatformSettings} disabled={saving}><Icon name="check" size={15} />{saving ? t('screens.accountSettings.savingSuffix') : t('screens.accountSettings.savePlatformSettings')}</BtnPrimary>
              )}
              {activeTab === 'organization' && (
                <BtnPrimary onClick={handleSaveOrgLanguage} disabled={saving}><Icon name="check" size={15} />{saving ? t('screens.accountSettings.savingSuffix') : t('screens.accountSettings.saveOrganizationSettings')}</BtnPrimary>
              )}
              {tenant.saved && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#ecfdf5', borderRadius: 8, fontSize: 13, color: '#10b981', fontWeight: 500 }}>
                  <Icon name="check" size={14} color="#10b981" /> {t('screens.accountSettings.saved')}
                </div>
              )}
              {tenant.error && TENANT_CONFIG_TABS.has(activeTab) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#fffbeb', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
                  {tenant.error}
                </div>
              )}
              {TENANT_CONFIG_TABS.has(activeTab) && (
                <BtnPrimary onClick={tenant.save} disabled={tenant.saving}>
                  <Icon name="check" size={15} />
                  {tenant.saving ? t('screens.accountSettings.savingSuffix') : t('screens.accountSettings.save')}
                </BtnPrimary>
              )}
            </div>
          }
        />

        <div style={{ display: 'flex', height: 'calc(100vh - 89px)' }}>
          {/* Sidebar tabs */}
          <div style={{ width: 220, background: '#fff', borderRight: `1px solid ${S.borderLight}`, padding: '20px 10px', flexShrink: 0 }}>
            {TABS.map(t => (
              <div key={t.id} onClick={() => setActiveTab(t.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, cursor: 'pointer', marginBottom: 4, background: activeTab === t.id ? `${S.burgundy}10` : 'transparent', color: activeTab === t.id ? S.burgundy : S.textSecondary, fontWeight: activeTab === t.id ? 600 : 500, fontSize: 13 }}
                onMouseEnter={e => { if (activeTab !== t.id) e.currentTarget.style.background = S.bgCard; }}
                onMouseLeave={e => { if (activeTab !== t.id) e.currentTarget.style.background = 'transparent'; }}>
                <Icon name={t.icon} size={16} color={activeTab === t.id ? S.burgundy : '#94a3b8'} />
                {t.label}
              </div>
            ))}
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '32px 40px' }}>

            {/* ── Tenant configuration ─────────────────────── */}
            {activeTab === 'vocabulary' && (
              <div style={{ maxWidth: 760 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 6 }}>{t('screens.tenantSettings.tabVocabulary')}</div>
                <VocabularySection schema={tenant.schema} vocabulary={tenant.vocabulary} setTerm={tenant.setTerm} />
              </div>
            )}
            {activeTab === 'modules' && (
              <div style={{ maxWidth: 640 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 6 }}>{t('screens.tenantSettings.tabModules')}</div>
                <ModulesSection schema={tenant.schema} features={tenant.features} toggleModule={tenant.toggleModule} />
              </div>
            )}
            {activeTab === 'branding' && (
              <div style={{ maxWidth: 640 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 6 }}>{t('screens.tenantSettings.tabBranding')}</div>
                <BrandingSection branding={tenant.branding} setBranding={tenant.setBranding} config={tenantConfig} />
              </div>
            )}
            {activeTab === 'documents' && (
              <div style={{ maxWidth: 640 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 6 }}>
                  {t('screens.tenantSettings.tabDocuments', { defaultValue: 'Document Settings' })}
                </div>
                <DocumentSettingsSection
                  disclaimerText={tenant.disclaimerText}
                  setDisclaimerText={tenant.setDisclaimerText}
                />
                <CommunicationsTextSection
                  communicationsText={tenant.communicationsText}
                  setCommunicationsText={tenant.setCommunicationsText}
                />
                <SignatoriesSection
                  signatories={tenant.signatories}
                  setSignatories={tenant.setSignatories}
                  config={tenantConfig}
                />
              </div>
            )}
            {activeTab === 'storage' && (
              <div style={{ maxWidth: 640 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 6 }}>{t('screens.tenantSettings.tabStorage')}</div>
                <StorageSection storage={tenant.storage} />
              </div>
            )}

            {/* ── Locale & Display ─────────────────────────── */}
            {activeTab === 'locale' && (
              <div style={{ maxWidth: 600 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 6 }}>{t('screens.accountSettings.localeTitle')}</div>
                <div style={{ fontSize: 13, color: S.textMuted, marginBottom: 24 }}>
                  {t('screens.accountSettings.localeDesc')}
                </div>

                {sectionLabel(t('screens.accountSettings.currency'))}
                <select value={currency} onChange={e => setCurrency(e.target.value)} style={selectStyle}>
                  {/* Inherit is a real, selectable state — not the absence of a
                      choice. Without it the only way to stop overriding the
                      tenant was to never open this screen. */}
                  <option value=''>{t('screens.accountSettings.inheritFromTenant')}</option>
                  {/* withResolved, exactly as the region and timezone
                      selects use it. Without it a tenant configured in a
                      currency this list does not ship — BHD — could not see
                      their own setting in the dropdown, and any save from
                      this screen wrote one of the four listed currencies
                      over it. The list was the bound; it is not any more. */}
                  {withResolved(CURRENCIES, currency || tenantLocale.currency).map(c => (
                    <option key={c.code} value={c.code}>
                      {t(`screens.accountSettings.currency_${c.code}`, { defaultValue: c.label })}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: 12, color: S.textMuted, marginTop: 4 }}>
                  {/* Preview the value that will actually be used, formatted the
                      way the app formats money — the old hardcoded "12,345"
                      showed a comma to a tenant whose separator is an
                      apostrophe. */}
                  {t('screens.accountSettings.preview')} {formatCurrencyIn(currency || tenantLocale.currency, 12345)}
                </div>

                {sectionLabel(t('screens.accountSettings.dateFormat'))}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 4 }}>
                  <div onClick={() => setDateFormat('')}
                    style={{ padding: '12px 14px', border: `2px solid ${!dateFormat ? S.burgundy : S.borderLight}`, borderRadius: 8, cursor: 'pointer', background: !dateFormat ? `${S.burgundy}06` : '#fff', transition: 'all 0.15s', gridColumn: '1 / -1' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: !dateFormat ? S.burgundy : S.textPrimary }}>{t('screens.accountSettings.inheritFromTenant')}</div>
                    <div style={{ fontSize: 12, fontFamily: 'monospace', color: !dateFormat ? S.burgundy : S.textSecondary, marginTop: 4 }}>{tenantLocale.dateFormat || '—'}</div>
                  </div>
                  {DATE_FORMATS.map(df => (
                    <div key={df.fmt} onClick={() => setDateFormat(df.fmt)}
                      style={{ padding: '12px 14px', border: `2px solid ${dateFormat === df.fmt ? S.burgundy : S.borderLight}`, borderRadius: 8, cursor: 'pointer', background: dateFormat === df.fmt ? `${S.burgundy}06` : '#fff', transition: 'all 0.15s' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: dateFormat === df.fmt ? S.burgundy : S.textPrimary }}>{df.fmt}</div>
                      <div style={{ fontSize: 11, color: S.textMuted, marginTop: 2 }}>{t(`screens.accountSettings.${df.labelKey}`, { defaultValue: df.label })}</div>
                      <div style={{ fontSize: 12, fontFamily: 'monospace', color: dateFormat === df.fmt ? S.burgundy : S.textSecondary, marginTop: 4 }}>{df.example}</div>
                    </div>
                  ))}
                </div>

                {sectionLabel(t('screens.accountSettings.region'))}
                <select value={region} onChange={e => setRegion(e.target.value)} style={selectStyle}>
                  {!region && <option value=''>{t('screens.accountSettings.notConfigured')}</option>}
                  {withResolved(REGIONS, region).map(r => (
                    <option key={r.code} value={r.code}>
                      {t(`screens.accountSettings.region_${r.code}`, { defaultValue: r.label })}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: 12, color: S.textMuted, marginTop: 4 }}>{t('screens.accountSettings.regionDesc')}</div>

                {sectionLabel(t('screens.accountSettings.timezone'))}
                <select value={timezone} onChange={e => setTimezone(e.target.value)} style={selectStyle}>
                  {!timezone && <option value=''>{t('screens.accountSettings.notConfigured')}</option>}
                  {withResolved(TIMEZONES, timezone).map(tz => <option key={tz.code} value={tz.code}>{tz.label}</option>)}
                </select>
                <div style={{ fontSize: 12, color: S.textMuted, marginTop: 4 }}>{t('screens.accountSettings.timezoneDesc')}</div>

                {sectionLabel(t('screens.accountSettings.interfaceLanguage'))}
                <select value={language} onChange={e => setLanguage(e.target.value)} style={selectStyle}>
                  {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>

                <div style={{ marginTop: 28, padding: '14px 18px', background: '#f8fafc', border: `1px solid ${S.borderLight}`, borderRadius: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary, marginBottom: 4 }}>{t('screens.accountSettings.configPreview')}</div>
                  <div style={{ fontSize: 12, color: S.textMuted, lineHeight: 1.8 }}>
                    {t('screens.accountSettings.configPreviewCurrency')} <strong>{currency}</strong> · {t('screens.accountSettings.configPreviewDateFormat')} <strong>{dateFormat}</strong> · {t('screens.accountSettings.configPreviewRegion')} <strong>{region || t('screens.accountSettings.notConfigured')}</strong> · {t('screens.accountSettings.configPreviewTimezone')} <strong>{timezone || t('screens.accountSettings.notConfigured')}</strong> · {t('screens.accountSettings.configPreviewLanguage')} <strong>{language}</strong>
                  </div>
                </div>

                <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
                  <BtnSecondary onClick={handleSaveLocale}><Icon name="check" size={15} />{t('screens.accountSettings.saveSettings')}</BtnSecondary>
                  <BtnPrimary onClick={handleApplyAndReload}><Icon name="check" size={15} />{t('screens.accountSettings.saveApplyImmediately')}</BtnPrimary>
                </div>
              </div>
            )}

            {/* ── Platform Settings ─────────────────────────── */}
            {activeTab === 'platform' && (
              <div style={{ maxWidth: 600 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 6 }}>{t('screens.accountSettings.platformTitle')}</div>
                <div style={{ fontSize: 13, color: S.textMuted, marginBottom: 24 }}>{t('screens.accountSettings.platformDesc')}</div>

                <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 12, padding: '0 20px' }}>
                  <Toggle checked={notifications} onChange={setNotifications} label={t('screens.accountSettings.notificationsLabel')} desc={t('screens.accountSettings.notificationsDesc')} />
                  <Toggle checked={auditEmails} onChange={setAuditEmails} label={t('screens.accountSettings.auditEmailsLabel')} desc={t('screens.accountSettings.auditEmailsDesc')} />
                </div>

                {/* TOTP 2FA Panel */}
                <div style={{ marginTop: 24, background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 12, padding: '20px 24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: S.textPrimary }}>{t('screens.accountSettings.authAppTitle')}</div>
                      <div style={{ fontSize: 12, color: S.textMuted, marginTop: 2 }}>{t('screens.accountSettings.authAppDesc')}</div>
                    </div>
                    <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: totpEnabled ? '#ecfdf5' : '#f1f5f9', color: totpEnabled ? '#10b981' : '#94a3b8' }}>
                      {totpEnabled ? t('screens.accountSettings.enabled') : t('screens.accountSettings.disabled')}
                    </span>
                  </div>

                  {totpError && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: '#991b1b' }}>{totpError}</div>
                  )}

                  {totpStep === 'idle' && !totpEnabled && (
                    <BtnPrimary onClick={startTotpSetup} disabled={totpLoading} style={{ marginTop: 4 }}>
                      <Icon name="shield" size={15} />{totpLoading ? t('screens.accountSettings.settingUp') : t('screens.accountSettings.enable2FA')}
                    </BtnPrimary>
                  )}

                  {totpStep === 'idle' && totpEnabled && (
                    <BtnSecondary onClick={startTotpDisable}>
                      <Icon name="x" size={15} />{t('screens.accountSettings.disable2FA')}
                    </BtnSecondary>
                  )}

                  {totpStep === 'verifying' && totpQR && (
                    <div>
                      <div style={{ fontSize: 13, color: S.textSecondary, marginBottom: 12 }}>
                        {t('screens.accountSettings.scan2FAInstr')}
                      </div>
                      <img src={totpQR} alt={t('screens.accountSettings.qrAlt')} style={{ width: 160, height: 160, marginBottom: 12, border: `1px solid ${S.borderLight}`, borderRadius: 8 }} />
                      {totpSecret && (
                        <div style={{ fontSize: 11, color: S.textMuted, marginBottom: 12, fontFamily: 'monospace', background: S.bgCard, padding: '6px 10px', borderRadius: 6 }}>
                          {t('screens.accountSettings.manualKey', { key: totpSecret })}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <input
                          value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder={t('screens.accountSettings.sixDigitCode')} maxLength={6}
                          style={{ padding: '9px 12px', border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 16, fontFamily: 'monospace', width: 140, letterSpacing: 3, outline: 'none' }}
                        />
                        <BtnPrimary onClick={confirmTotpEnable} disabled={totpCode.length !== 6 || totpLoading}>
                          {totpLoading ? t('screens.accountSettings.verifying') : t('screens.accountSettings.verifyEnable')}
                        </BtnPrimary>
                        <BtnSecondary onClick={() => { setTotpStep('idle'); setTotpCode(''); setTotpError(''); }}>{t('screens.accountSettings.cancel')}</BtnSecondary>
                      </div>
                    </div>
                  )}

                  {totpStep === 'disabling' && (
                    <div>
                      <div style={{ fontSize: 13, color: S.textSecondary, marginBottom: 10 }}>
                        {t('screens.accountSettings.disableConfirm')}
                      </div>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <input
                          value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder={t('screens.accountSettings.sixDigitCode')} maxLength={6}
                          style={{ padding: '9px 12px', border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 16, fontFamily: 'monospace', width: 140, letterSpacing: 3, outline: 'none' }}
                        />
                        <BtnPrimary onClick={confirmTotpDisable} disabled={totpCode.length !== 6 || totpLoading}
                          style={{ background: '#ef4444' }}>
                          {totpLoading ? t('screens.accountSettings.disabling') : t('screens.accountSettings.confirmDisable')}
                        </BtnPrimary>
                        <BtnSecondary onClick={() => { setTotpStep('idle'); setTotpCode(''); setTotpError(''); }}>{t('screens.accountSettings.cancel')}</BtnSecondary>
                      </div>
                    </div>
                  )}
                </div>

                {/* Stripe Billing Portal.
                    Subscription and payment method are tenant-commercial
                    concerns, not personal settings — gated on orgs:update
                    (ORG_ADMINS), the same permission that governs changing
                    the organisation. A claimsManager was being offered
                    "update payment method / change your plan". */}
                {canManageBilling && <div style={{ marginTop: 24, background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 12, padding: '20px 24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: S.textPrimary }}>{t('screens.accountSettings.billingTitle')}</div>
                      <div style={{ fontSize: 12, color: S.textMuted, marginTop: 2 }}>{t('screens.accountSettings.billingDesc')}</div>
                    </div>
                  </div>
                  {portalError && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: '#991b1b' }}>{portalError}</div>
                  )}
                  <BtnPrimary onClick={openBillingPortal} disabled={portalLoading} style={{ marginTop: 4 }}>
                    <Icon name="credit-card" size={15} />{portalLoading ? t('screens.accountSettings.opening') : t('screens.accountSettings.manageSubscription')}
                  </BtnPrimary>
                </div>}

                <div style={{ marginTop: 24 }}>
                  <BtnPrimary onClick={handleSavePlatformSettings} disabled={saving}>
                    <Icon name="check" size={15} />{saving ? t('screens.accountSettings.savingSuffix') : t('screens.accountSettings.savePlatformSettings')}
                  </BtnPrimary>
                </div>
              </div>
            )}

            {/* ── Organization Settings ──────────────────────── */}
            {activeTab === 'organization' && isOrgAdmin && (
              <div style={{ maxWidth: 600 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 6 }}>{t('screens.accountSettings.orgTitle')}</div>
                <div style={{ fontSize: 13, color: S.textMuted, marginBottom: 24 }}>{t('screens.accountSettings.orgDesc')}</div>
                {!userOrgId && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px', marginBottom: 16, fontSize: 13, color: '#991b1b' }}>
                    {t('screens.accountSettings.orgIdMissing')}
                  </div>
                )}

                {sectionLabel(t('screens.accountSettings.orgProfile'))}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                  <div>
                    <div style={{ fontSize: 11, color: S.textMuted, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>{t('screens.accountSettings.orgName')}</div>
                    <input value={orgName} onChange={e => setOrgName(e.target.value)} style={selectStyle} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: S.textMuted, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>{t('screens.accountSettings.orgPhone')}</div>
                    <input value={orgPhone} onChange={e => setOrgPhone(e.target.value)} style={selectStyle} />
                  </div>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, color: S.textMuted, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>{t('screens.accountSettings.orgAddress')}</div>
                  <input value={orgAddress} onChange={e => setOrgAddress(e.target.value)} style={selectStyle} />
                </div>

                {sectionLabel(t('screens.accountSettings.country'))}
                <input value={orgCountry} onChange={e => setOrgCountry(e.target.value.toUpperCase())} placeholder={t('screens.accountSettings.countryPlaceholder')} maxLength={2} style={selectStyle} />

                {sectionLabel(t('screens.accountSettings.industry'))}
                <input value={orgIndustry} onChange={e => setOrgIndustry(e.target.value)} placeholder={t('screens.accountSettings.industryPlaceholder')} style={selectStyle} />

                {sectionLabel(t('screens.accountSettings.timezone'))}
                <input value={orgTimezone} onChange={e => setOrgTimezone(e.target.value)} placeholder={t('screens.accountSettings.orgTimezonePlaceholder')} style={selectStyle} />

                {sectionLabel(t('screens.accountSettings.currency'))}
                <input value={orgCurrency} onChange={e => setOrgCurrency(e.target.value.toUpperCase())} placeholder={t('screens.accountSettings.currencyIsoPlaceholder')} maxLength={3} style={selectStyle} />

                {sectionLabel(t('screens.accountSettings.defaultLanguage'))}
                <select value={orgLanguage} onChange={e => setOrgLanguage(e.target.value)} style={selectStyle}>
                  {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>
                <div style={{ fontSize: 12, color: S.textMuted, marginTop: 4 }}>
                  {t('screens.accountSettings.defaultLanguageDesc')}
                </div>

                <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
                  <BtnPrimary onClick={handleSaveOrgLanguage} disabled={saving || !userOrgId}><Icon name="check" size={15} />{saving ? t('screens.accountSettings.savingSuffix') : t('screens.accountSettings.saveOrganizationSettings')}</BtnPrimary>
                </div>

                {saved && (
                  <div style={{ marginTop: 16, padding: '10px 14px', background: '#dcfce7', border: '1px solid #86efac', borderRadius: 8, fontSize: 13, color: '#15803d' }}>
                    ✓ {t('screens.accountSettings.orgSavedOk')}
                  </div>
                )}
                {saveError && (
                  <div style={{ marginTop: 16, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#991b1b' }}>
                    {saveError}
                  </div>
                )}
              </div>
            )}

            {/* ── Profile ───────────────────────────────────── */}
            {activeTab === 'profile' && (
              <div style={{ maxWidth: 600 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 6 }}>{t('screens.accountSettings.profileTitle')}</div>
                <div style={{ fontSize: 13, color: S.textMuted, marginBottom: 24 }}>{t('screens.accountSettings.profileDesc')}</div>

                <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 12, padding: 24 }}>
                  {/* Avatar */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, paddingBottom: 24, borderBottom: `1px solid ${S.borderLight}` }}>
                    <div style={{ width: 64, height: 64, borderRadius: 14, background: `linear-gradient(135deg, ${S.burgundy}, #722f37)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 22, fontWeight: 700 }}>
                      {userData?.name ? userData.name.slice(0, 2).toUpperCase() : 'U'}
                    </div>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary }}>{userData?.name || '—'}</div>
                      <div style={{ fontSize: 13, color: S.textMuted, marginTop: 2 }}>{userData?.email || '—'}</div>
                      <div style={{ fontSize: 12, marginTop: 4, padding: '2px 8px', background: `${S.burgundy}10`, color: S.burgundy, borderRadius: 4, display: 'inline-block', fontWeight: 600 }}>
                        {userData?.role || 'Adjuster'}
                      </div>
                    </div>
                  </div>

                  {[
                    { label: t('screens.accountSettings.fieldFullName'),  value: userData?.name,    icon: 'user' },
                    { label: t('screens.accountSettings.fieldEmail'),      value: userData?.email,   icon: 'mail' },
                    { label: t('screens.accountSettings.fieldPhone'),      value: userData?.phone,   icon: 'phone' },
                    { label: t('screens.accountSettings.fieldAddress'),    value: userData?.address, icon: 'map-pin' },
                    { label: t('screens.accountSettings.fieldRole'),       value: userData?.role,    icon: 'shield' },
                  ].map(field => (
                    <div key={field.label} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                      <div style={{ width: 34, height: 34, borderRadius: 8, background: S.bgCard, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon name={field.icon} size={16} color={S.textMuted} />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: S.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>{field.label}</div>
                        <div style={{ fontSize: 13, color: field.value ? S.textPrimary : S.textMuted, fontWeight: 500 }}>{field.value || '—'}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: 20 }}>
                  <BtnPrimary onClick={() => window.location.hash = '#/my-profile'}><Icon name="edit" size={15} />{t('screens.accountSettings.editProfile')}</BtnPrimary>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default AccountSettingsScreen;
